import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { InlineLoading, LoadingButton } from "./Spinner";
import CodeViewer, { detectLangFromContentType } from "./CodeViewer";
import VarText from "./VarText";

interface Props {
  requestId: string;
  // The revision to focus on, plus the immediately-previous revision
  // (for the diff). When the focused revision is the very first one,
  // prev is null.
  current: { id: string; revision_no: number } | null;
  prev: { id: string; revision_no: number } | null;
  onClose: () => void;
  onRestored: () => void;
}

// Portaled modal that shows the full snapshot of a request revision —
// mirrors RunDetailModal's layout and keyboard / backdrop behavior. The
// user can restore from here (which writes a NEW revision so nothing is
// lost). The pluggable RevisionDiff renders below when there's a
// previous version to compare against.
export default function RevisionDetailModal({ requestId, current, prev, onClose, onRestored }: Props) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["revision", requestId, current?.id],
    queryFn: () => api.requests.revision(requestId, current!.id),
    enabled: current != null,
  });
  const restore = useMutation({
    mutationFn: () => api.requests.restoreRevision(requestId, current!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["request", requestId] });
      qc.invalidateQueries({ queryKey: ["revisions", requestId] });
      onRestored();
      onClose();
    },
  });

  useEffect(() => {
    if (!current) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [current, onClose]);

  if (!current) return null;
  const rev = q.data?.revision ?? null;
  const ct = rev ? findHeader(rev.headers ?? [], "content-type") : null;
  const lang = detectLangFromContentType(ct);

  return createPortal(
    <div
      className="fixed inset-0 z-[110] grid place-items-center p-4"
      style={{ background: "var(--backdrop)" }}
      onClick={onClose}
    >
      <div
        className="card w-full max-w-5xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
          <h2 className="text-sm font-semibold">Revision details</h2>
          <span className="chip bg-surface-3 muted-strong mono">#{current.revision_no}</span>
          {rev && (
            <span className="text-xs muted-strong">
              {new Date(rev.created_at).toLocaleString()}
              {rev.author_name ? ` · ${rev.author_name}` : rev.author_email ? ` · ${rev.author_email}` : ""}
            </span>
          )}
          <LoadingButton
            className="btn ml-auto"
            pending={restore.isPending}
            onClick={() => restore.mutate()}
          >Restore this version</LoadingButton>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
          {!rev && <InlineLoading label="Loading revision…" />}
          {rev && (
            <>
              <section className="space-y-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={"method-tag shrink-0 " + rev.method.toLowerCase()}>{rev.method}</span>
                  <VarText value={rev.url ?? ""} className="text-xs font-mono truncate flex-1 min-w-0" />
                </div>
                {rev.name && <div className="text-sm font-medium">{rev.name}</div>}
                {rev.note && (
                  <div className="text-xs muted">Note: <span className="text-text">{rev.note}</span></div>
                )}
              </section>

              {/* Snapshot: query, headers, body. KV blocks render with
                  VarText so {{vars}} look identical to the URL row. */}
              <KVBlock label="Query" pairs={rev.query ?? []} />
              <KVBlock label="Headers" pairs={rev.headers ?? []} />
              {rev.body && (
                <section className="space-y-1.5 min-w-0">
                  <div className="label">Body <span className="muted-strong">({rev.body_type})</span></div>
                  <CodeViewer value={rev.body} lang={lang} />
                </section>
              )}

              {/* Diff against the prior version. RevisionDiff lives in
                  RequestBuilder.tsx; importing it would be a cycle, so
                  we inline the same component here as a thin wrapper. */}
              {prev && (
                <section className="space-y-1.5 min-w-0 border-t border-border pt-3">
                  <div className="label">Changes from #{prev.revision_no} → #{current.revision_no}</div>
                  <RevDiffInline requestId={requestId} fromId={prev.id} toId={current.id} />
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function KVBlock({ label, pairs }: { label: string; pairs: Array<{ key: string; value: string; enabled?: boolean }> }) {
  if (!pairs.length) return null;
  return (
    <section className="space-y-1.5 min-w-0">
      <div className="label">{label} <span className="muted-strong">({pairs.length})</span></div>
      <table className="w-full text-xs border border-border rounded">
        <tbody>
          {pairs.map((p, i) => (
            <tr key={i} className="border-b border-border last:border-0 align-top">
              <th className="text-left font-mono font-normal text-text-dim p-1.5 pr-3 w-[42%] break-all">{p.key}</th>
              <td className="font-mono text-text p-1.5 break-all">
                <VarText value={p.value} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// Inline copy of the diff component, fetched fresh against the same
// query keys as the inline RevisionsPane usage. Defined here to keep
// RequestBuilder.tsx from owning an import cycle.
function RevDiffInline({ requestId, fromId, toId }: { requestId: string; fromId: string; toId: string }) {
  const fromQ = useQuery({
    queryKey: ["revision", requestId, fromId],
    queryFn: () => api.requests.revision(requestId, fromId),
  });
  const toQ = useQuery({
    queryKey: ["revision", requestId, toId],
    queryFn: () => api.requests.revision(requestId, toId),
  });
  if (fromQ.isLoading || toQ.isLoading) return <InlineLoading label="Loading diff…" />;
  const a = fromQ.data?.revision;
  const b = toQ.data?.revision;
  if (!a || !b) return <p className="text-xs muted italic">Couldn't load one or both revisions.</p>;

  const scalars: Array<{ field: string; from: string; to: string }> = [
    { field: "name",        from: a.name ?? "",        to: b.name ?? "" },
    { field: "method",      from: a.method,            to: b.method },
    { field: "URL",         from: a.url ?? "",         to: b.url ?? "" },
    { field: "body type",   from: a.body_type,         to: b.body_type },
    { field: "description", from: a.description ?? "", to: b.description ?? "" },
  ].filter((s) => s.from !== s.to);

  const bodyChanged = (a.body ?? "") !== (b.body ?? "");
  const anyChange = scalars.length > 0 || bodyChanged;
  if (!anyChange) return <p className="text-xs muted italic">No differences.</p>;

  return (
    <ul className="space-y-0.5 text-xs">
      {scalars.map((s) => (
        <li key={s.field} className="flex items-baseline gap-2 min-w-0">
          <span className="muted-strong shrink-0 capitalize">{s.field}</span>
          <span className="mono text-[11px] flex-1 min-w-0 break-all">
            <span className="muted line-through">{s.from || "∅"}</span>
            <span className="muted-strong mx-1.5">→</span>
            <span className="text-text">{s.to || "∅"}</span>
          </span>
        </li>
      ))}
      {bodyChanged && (
        <li className="muted-strong">Body changed ({((b.body ?? "").length - (a.body ?? "").length >= 0 ? "+" : "")}{(b.body ?? "").length - (a.body ?? "").length} chars)</li>
      )}
    </ul>
  );
}

function findHeader(pairs: Array<{ key: string; value: string }>, name: string): string | null {
  const m = pairs.find((p) => p.key.toLowerCase() === name.toLowerCase());
  return m ? m.value : null;
}
